import ProgressBar from "@/components/ProgressBar";
import { Tag } from "@/components/Tag";
import { NewTag } from "@/components/Tag";
import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import CardRow from "@/containers/CardRow";
import { binIcon, copyIcon, favorite } from "@/designSystemIcons";
import { formatTime } from "@/functions/formatTime";
import { cn } from "@/lib/utils";
import { getFormationsLabel } from "@/redux/loadedContent/loadedContentSlice";
import { Tactic } from "@/redux/tactics/tacticsSlice";
import { Pen } from "lucide-react";
import { useSelector } from "react-redux";

import TacticImage from "./TacticImage";

interface TitleProps {
  title: string;
  eaCode: string;
  isFavorite?: boolean;
  isNew?: boolean;
  handleCopy: (eaCode: string) => void;
  handleClickFavorite: () => void;
  canEdit: boolean;
  handleEditTactic: () => void;
  handleDeleteTactic: () => void;
}

const TitleSection = ({
  title,
  eaCode,
  isFavorite,
  isNew,
  handleCopy,
  handleClickFavorite,
  canEdit,
  handleEditTactic,
  handleDeleteTactic,
}: TitleProps) => {
  return (
    <div className="flex flex-row items-center justify-between px-2 touch-device:px-0 pb-1 touch-device:pb-2 touch-device:pr-2 text-xs touch-device:pl-2 large:pl-4 touch-device:py-2 touch-device:text-sm touch-device:border-b border-white/5">
      <div className="flex flex-row justify-between grow desktop:gap-x-2 items-center mr-2">
        <p className="line-clamp-1 font-medium text-sm">{title}</p>
        {isNew && (
          <div className="text-xs text-white">
            <NewTag type="new-design" />
          </div>
        )}
        {canEdit && (
          <div className="ml-2 hidden group-hover:flex flex-row -m-2">
            <Tooltip content="Edit Tactic" delayDuration={150}>
              <button
                className="text-xs text-gray-300 hover:text-gray-100 p-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditTactic();
                }}
              >
                <Pen size={16} />
              </button>
            </Tooltip>

            <Tooltip content="Delete Tactic" delayDuration={150}>
              <button
                className="text-xs text-gray-300 hover:text-gray-100 p-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTactic();
                }}
              >
                {binIcon}
              </button>
            </Tooltip>
          </div>
        )}
      </div>
      <div className="flex flex-row items-center gap-2 pr-2 touch-device:pr-0">
        <Tooltip content="Copy Tactic Code" delayDuration={150}>
          <button
            className="text-sm gap-x-1 text-dark-tertiary hover:text-dark-secondary group/copy hidden touch-device:flex items-center"
            onClick={(e) => {
              handleCopy(eaCode);
              e.stopPropagation();
            }}
          >
            <p>{eaCode}</p>
            <span className="fill-dark-tertiary group-hover/copy:fill-dark-secondary">
              {copyIcon}
            </span>
          </button>
        </Tooltip>
        <Tooltip content="Add to Favorites" delayDuration={150}>
          <button
            className={cn(
              "text-dark-tertiary desktop:hover:text-dark-secondary w-5 h-5",
              isFavorite ? "text-white" : "text-dark-tertiary",
            )}
            onClick={(e) => {
              handleClickFavorite();
              e.stopPropagation();
            }}
          >
            {favorite}
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

interface InfoSectionProps {
  tags: string[];
  description: string;
  formationName: string;
  buildUpStyle: string;
  defensiveApproach: string;
  lineHeight: number;
  publishedAt: number;
  popularityScore: number;
}

const InfoSectionMobile = ({
  tags,
  formationName,
  buildUpStyle,
  defensiveApproach,
  lineHeight,
  publishedAt,
  popularityScore,
}: InfoSectionProps) => {
  const publishedAtString =
    formatTime(new Date().getTime() - publishedAt) + " ago";
  return (
    <div className="flex flex-row justify-between text-xs w-full">
      <div className="flex flex-col gap-y-1 text-dark-tertiary w-full">
        <div className="flex flex-row gap-x-1 pl-2 pr-4">
          {tags.map((tag) => (
            <Tag tag={tag} key={tag} type="new-design" />
          ))}
        </div>
        <div className="flex flex-row items-center pl-2 pr-4">
          <p className="flex-1 text-left">{formationName}</p>
          <p className="w-1/3 text-center">{buildUpStyle}</p>
          <p className="flex-1 text-right">
            {defensiveApproach + " " + lineHeight}
          </p>
        </div>
        <div className="flex flex-row items-center gap-x-1 py-1 bg-black/20 pl-2 pr-4">
          <div className="flex flex-row items-center w-full gap-x-1">
            <p className="text-sm text-white w-7">{popularityScore}</p>
            <div className="w-20">
              <ProgressBar
                value={popularityScore}
                totalValue={10}
                small={true}
              />
            </div>
          </div>

          <p className="whitespace-nowrap">{publishedAtString}</p>
        </div>
      </div>
    </div>
  );
};

const InfoSection = ({
  tags,
  description,
  formationName,
  buildUpStyle,
  defensiveApproach,
  lineHeight,
  publishedAt,
  popularityScore,
}: InfoSectionProps) => {
  const publishedAtString =
    formatTime(new Date().getTime() - publishedAt) + " ago";

  return (
    <div className="flex flex-row h-full text-xs">
      <div className="flex flex-col w-full h-full px-2 py-2 border-r large:px-4 min-w-32 gap-y-1 border-white/5">
        <div className="flex flex-row gap-x-1">
          {tags.map((tag) => (
            <Tag tag={tag} key={tag} type="new-design" />
          ))}
        </div>
        <p className="text-xs line-clamp-2 text-dark-tertiary">{description}</p>
      </div>
      <div className="flex flex-row items-center w-full px-2 py-2 text-center border-r border-white/5 max-w-80 grow">
        <p className="w-full">{formationName}</p>
        <p className="w-full">{buildUpStyle}</p>
        <p className="w-full">{defensiveApproach + ": " + lineHeight}</p>
      </div>

      <div className="flex flex-col items-center justify-center px-4 py-2 border-r large:px-6 w-fit gap-y-2 border-white/5 min-w-32 large:min-w-36 max-w-40 text-dark-tertiary">
        <Tooltip
          content={
            <p className="w-64 py-2">
              The Popularity Score gives you an idea of how much a tactic is
              used in-game. It is based on Club Import data, which makes it a
              perfect indicator for which tactics are currently preferred by FC
              players. The score is updated once every week in order to make
              sure that it is always up-to-date with the latest meta!
            </p>
          }
          side="top"
          delayDuration={400}
        >
          <p>Popularity Score</p>
          <div className="flex flex-row items-center w-full gap-x-1">
            <ProgressBar value={popularityScore} totalValue={10} small={true} />
            <p className="text-sm text-white">{popularityScore}</p>
          </div>
        </Tooltip>
      </div>
      <div className="flex flex-row items-center justify-center px-4 py-2 whitespace-nowrap large:flex min-w-24 max-w-40 text-dark-tertiary">
        <p>{publishedAtString}</p>
      </div>
    </div>
  );
};

interface TacticCardProps {
  tactic: Tactic;
  active?: boolean;
  handleClickTacticCard: (tactic: Tactic) => void;
  handleCopy: (eaCode: string) => void;
  handleClickFavorite: () => void;
  handleEditTactic: () => void;
  handleDeleteTactic: () => void;
}

const TacticCard = ({
  tactic,
  active,
  handleClickTacticCard,
  handleCopy,
  handleClickFavorite,
  handleEditTactic,
  handleDeleteTactic,
}: TacticCardProps) => {
  const formationName = useSelector(
    getFormationsLabel(tactic.teamTactics.formationId),
  );
  return (
    <CardRow active={active} onClick={() => handleClickTacticCard(tactic)}>
      <TacticImage
        imageUrl={tactic.logoUrl}
        author={tactic.author}
        isTrending={tactic.isTrending}
        rounded={true}
        hideAuthors={true}
      />
      <div className="flex flex-col py-1 touch-device:px-0 touch-device:py-0 grow">
        <TitleSection
          title={tactic.name}
          eaCode={tactic.eaCode}
          isFavorite={tactic.isFavorite}
          isNew={tactic.isNew}
          handleCopy={handleCopy}
          handleClickFavorite={handleClickFavorite}
          canEdit={tactic.canEdit}
          handleEditTactic={handleEditTactic}
          handleDeleteTactic={handleDeleteTactic}
        />
        <span className="hidden h-full touch-device:block">
          <InfoSection
            tags={tactic.tags}
            description={tactic.description}
            formationName={formationName}
            buildUpStyle={tactic.teamTactics.buildUpStyle}
            defensiveApproach={tactic.teamTactics.defensiveApproach}
            lineHeight={tactic.teamTactics.lineHeight}
            publishedAt={tactic.publishedAt}
            popularityScore={tactic.popularityScore}
          />
        </span>
        <span className="block my-auto touch-device:hidden">
          <InfoSectionMobile
            tags={tactic.tags}
            description={tactic.description}
            formationName={formationName}
            buildUpStyle={tactic.teamTactics.buildUpStyle}
            defensiveApproach={tactic.teamTactics.defensiveApproach}
            lineHeight={tactic.teamTactics.lineHeight}
            publishedAt={tactic.publishedAt}
            popularityScore={tactic.popularityScore}
          />
        </span>
      </div>
    </CardRow>
  );
};

export default TacticCard;
